import { useState, useEffect } from "react";
import {
  HiOutlineBookOpen, HiOutlineArrowDownTray, HiOutlineArrowTopRightOnSquare,
  HiOutlinePlayCircle, HiOutlineDocumentText, HiOutlineLockClosed,
} from "react-icons/hi2";
import PortalLayout from "./PortalLayout";
import { portalAPI } from "./portalApi";
import "./ResourcesPage.css";

const fmtSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const TYPE_ICON = {
  file:  HiOutlineDocumentText,
  link:  HiOutlineArrowTopRightOnSquare,
  video: HiOutlinePlayCircle,
};

export default function ResourcesPage() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    portalAPI.resources()
      .then(res => setData(res?.data || null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const hasAny = data && data.total > 0;

  return (
    <PortalLayout eyebrow="Resources" pageTitle="Resource Library"
      quote="Preparation is its own kind of courage." quoteAuthor="Forge Retreat">

      {loading ? (
        <div className="rs__loading"><div className="rs__spinner" /></div>
      ) : !hasAny ? (
        <div className="pc rs__empty">
          <HiOutlineBookOpen className="rs__empty-icon" />
          <p className="rs__empty-title">Nothing here yet</p>
          <p className="rs__empty-sub">
            Guides, forms, and preparation materials will appear here as our team
            adds them. Some resources also unlock as you move through your journey.
          </p>
        </div>
      ) : (
        <>
          <p className="rs__intro">
            Guides, forms, and materials to help you prepare. Some unlock as you
            progress — if something isn't showing yet, it will once you reach that step.
          </p>

          {data.categories.map(cat => (
            <div key={cat.id} className="rs__section">
              <p className="p-section-title rs__section-title">{cat.name}</p>
              <div className="rs__grid">
                {cat.resources.map(r => {
                  const Icon = TYPE_ICON[r.type] || HiOutlineDocumentText;
                  const isExternal = r.type !== "file";
                  const href = isExternal
                    ? r.external_url
                    : portalAPI.resourceDownloadUrl(r.id);

                  return (
                    <a key={r.id} href={href} className="pc rs__card"
                      {...(isExternal
                        ? { target: "_blank", rel: "noreferrer" }
                        : {})}>
                      <div className="rs__card-icon"><Icon /></div>
                      <div className="rs__card-body">
                        <p className="rs__card-title">{r.title}</p>
                        {r.description && <p className="rs__card-sub">{r.description}</p>}
                        <p className="rs__card-meta">
                          {r.type === "file"
                            ? `${(r.file_name || "").split(".").pop()?.toUpperCase() || "FILE"}${r.file_size ? ` · ${fmtSize(r.file_size)}` : ""}`
                            : r.type === "video" ? "Video" : "External link"}
                          {r.visibility !== "all" && (
                            <span className="rs__gated">
                              <HiOutlineLockClosed /> {r.visibility === "alumni" ? "Alumni" : r.visibility === "assigned" ? "Confirmed" : "Cleared"}
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="rs__card-action">
                        {isExternal ? <HiOutlineArrowTopRightOnSquare /> : <HiOutlineArrowDownTray />}
                      </div>
                    </a>
                  );
                })}
              </div>
            </div>
          ))}
        </>
      )}
    </PortalLayout>
  );
}
